import React, { useState, useEffect } from 'react';
import UserLayout from '../components/UserLayout';
import { apiClient } from '../api/api';
import { Package, Send, Video, Search, Filter, ArrowUpRight, ChevronLeft, ChevronRight } from 'lucide-react';
import Loader from '../components/Loader';
import './AffiliatesPage.css';
import { useAuth } from '../context/AuthContext'; // Assuming useAuth is from AuthContext

const AffiliatesPage = () => {
  const [sampleFunnel, setSampleFunnel] = useState(null);
  const [affiliates, setAffiliates] = useState([]);
  const [productSkus, setProductSkus] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const { selectedShopId, dateRange } = useAuth();

  useEffect(() => {
    fetchAffiliatesData();
  }, [selectedShopId, dateRange]);

  const fetchAffiliatesData = async () => {
    try {
      setLoading(true);
      let url = `/affiliates-metrics?range=${dateRange}`;
      if (selectedShopId) {
        url += `&shop_id=${selectedShopId}`;
      }
      const response = await apiClient.get(url, {
        withCredentials: true,
        credentials: 'include'
      });
      console.log("Affiliates Data:", response.data); // Debugging
      
      setSampleFunnel(response.data.sample_funnel);
      setAffiliates(response.data.affiliates);
      setProductSkus(response.data.product_skus || []);
    } catch (error) {
      console.error('Error fetching affiliates data:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredAffiliates = affiliates.filter(affiliate =>
    affiliate.creator.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <UserLayout>
      <div className="page-relative-wrapper">
        <div className="affiliates-page-container">
        
        {/* Sample Journey Funnel */}
        <div className="funnel-section">
          <div className="funnel-header">
            <h2 className="funnel-title">Sample Journey Funnel</h2>
            <div className="funnel-badges">
              <div className="badge-active">
                {affiliates.length} Active
              </div>
              <div className="badge-growth">
                +0% from last week
              </div>
            </div>
          </div>

          <div className="funnel-grid">
            {/* Card 1: Samples Requested */}
            <div className={`funnel-card requested ${loading ? 'shimmer skeleton' : ''}`}>
              <div className="card-icon-wrapper yellow">
                <Package size={24} />
              </div>
              <div className="card-value">{sampleFunnel?.samples_requested || 0}</div>
              <div className="card-label">Samples Requested</div>
            </div>

            {/* Card 2: Samples Sent */}
            <div className={`funnel-card sent ${loading ? 'shimmer skeleton' : ''}`}>
              <div className="card-icon-wrapper blue">
                <Send size={24} />
              </div>
              <div className="card-value">{sampleFunnel?.samples_sent || 0}</div>
              <div className="card-label">Samples Sent</div>
            </div>

            {/* Card 3: Posts Created */}
            <div className={`funnel-card created ${loading ? 'shimmer skeleton' : ''}`}>
              <div className="card-icon-wrapper green">
                <Video size={24} />
              </div>
              <div className="card-value">{sampleFunnel?.posts_created || 0}</div>
              <div className="card-label">Posts Created</div>
            </div>
          </div>

          <div className="funnel-metrics-row">
            <div className="metric-item">
              <div className="metric-label-small">Conversion Rate</div>
              <div className="metric-value-large">
                  {sampleFunnel?.conversion_rate || '0%'}
              </div>
            </div>
            <div className="metric-item">
              <div className="metric-label-small">Content Rate</div>
              <div className="metric-value-large green">
                {sampleFunnel?.content_rate || '0%'}
              </div>
            </div>
             <div className="metric-item">
              <div className="metric-label-small">Avg. daily creators posted content</div>
              <div className="metric-value-large">
                {(sampleFunnel?.avg_time_to_post || '0').toString().replace(/ days/g, '')}
              </div>
            </div>
          </div>
        </div>

        {/* Product SKU Performance Table */}
        {productSkus.length > 0 && (
          <div className="table-section sku-table-override" style={{ marginBottom: '24px' }}>
            <div className="table-header">
              <h2 className="table-title">Product SKU Performance</h2>
              <div className="badge-active">{productSkus.length} SKUs</div>
            </div>
            <table className={`affiliates-table ${loading ? 'shimmer skeleton' : ''}`}>
              <thead>
                <tr>
                  <th>Product Name</th>
                  <th>Samples Requested</th>
                  <th>Samples Shipped</th>
                  <th>Content GMV</th>
                </tr>
              </thead>
              <tbody>
                {productSkus.map((sku, index) => (
                  <tr key={index}>
                    <td>
                      <div className="creator-cell" style={{ maxWidth: '320px' }}>
                        <div className="creator-details">
                          <span className="creator-name" style={{ whiteSpace: 'normal', lineHeight: '1.4' }}>
                            {sku.product_name?.length > 70 ? sku.product_name.substring(0, 70) + '...' : sku.product_name}
                          </span>
                        </div>
                      </div>
                    </td>
                    <td>{sku.samples_requested}</td>
                    <td>{sku.samples_shipped}</td>
                    <td>
                      <div className="gmv-cell">
                        <span>{sku.content_gmv}</span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Top Performing Affiliates */}
        <div className="table-section">
          <div className="table-header">
            <h2 className="table-title">Top Performing Affiliates</h2>
            <div className="table-controls">
              <div className="search-wrapper">
                <Search className="search-icon" />
                <input 
                  type="text" 
                  placeholder="Search creators..." 
                  className="search-input"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              <button className="filter-btn">
                <Filter size={16} /> Filter
              </button>
              <button className="clear-filter-btn">
                Clear Filters
              </button>
            </div>
          </div>

          <table className={`affiliates-table ${loading ? 'shimmer skeleton' : ''}`}>
            <thead>
              <tr>
                <th>Creator</th>
                <th>Affiliate GMV</th>
                <th>Est. Commission</th>
                <th>AOV</th>
                <th>CTR</th>
                <th>Orders</th>
              </tr>
            </thead>
            <tbody>
              {filteredAffiliates.map((affiliate, index) => {
                 // Clean up currency strings/numbers
                 const parseCurrency = (val) => {
                     if (typeof val === 'number') return val;
                     if (typeof val === 'string' && val !== '--') return parseFloat(val.replace(/[^0-9.-]+/g,""));
                     return 0;
                 };

                 const gmv = parseCurrency(affiliate.affiliate_gmv);
                 const commission = parseCurrency(affiliate.est_commission);
                 
                 // Correctly map orders (API returns 'orders')
                 const orders = parseInt(affiliate.orders || affiliate.affiliate_orders || 0, 10);
                 
                 // Handle CTR -- or %
                 const ctr = affiliate.ctr === '--' ? '0%' : affiliate.ctr;
                 
                 // User requested AOV column, API returns 'aov' or fallback
                 const aov = affiliate.aov || affiliate.avg_affiliate_customers || '0';

                 return (
                  <tr key={index}>
                    <td>
                      <div className="creator-cell">
                        <div className="creator-avatar">
                          {affiliate.avatar ? (
                             <img src={affiliate.avatar} alt={affiliate.creator} referrerPolicy="no-referrer" />
                          ) : (
                             <div className="placeholder">{affiliate.creator ? affiliate.creator.charAt(1).toUpperCase() : '?'}</div>
                          )}
                        </div>
                        <div className="creator-details">
                          <span className="creator-name">{affiliate.creator ? affiliate.creator.replace('@','') : 'Unknown'}</span>
                          <span className="creator-handle">{affiliate.creator}</span>
                          <span className="creator-handle" style={{fontSize:'0.75rem', marginTop:'2px'}}>{affiliate.followers || affiliate.aff_followers || '0'} followers</span>
                        </div>
                      </div>
                    </td>
                    <td>
                      <div className="gmv-cell">
                        <span>${isNaN(gmv) ? '0.00' : gmv.toFixed(2)}</span>
                        {/* <div className="gmv-sub">
                            <ArrowUpRight size={12} color="#10b981" /> 0%
                        </div> */}
                      </div>
                    </td>
                    <td>
                        <div className="commission-cell">
                            <span>${isNaN(commission) ? '0.00' : commission.toFixed(2)}</span>
                           {/* <span className="commission-sub">0%</span> */}
                        </div>
                    </td>
                    <td>{aov}</td>
                    <td>{ctr || '0%'}</td>
                    <td>{orders}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          
          <div className="pagination-footer">
             <span>Showing {filteredAffiliates.length} of {affiliates.length} affiliates</span>
             <div className="pagination-controls">
                <button className="page-btn"><ChevronLeft size={16}/></button>
                <button className="page-btn active">1</button>
                <button className="page-btn">2</button>
                <button className="page-btn">3</button>
                <button className="page-btn"><ChevronRight size={16}/></button>
             </div>
          </div>
        </div>
        </div>
      </div>
    </UserLayout>
  );
};

export default AffiliatesPage;
